import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { BlogPost } from '@/content/types';

const TABLE = 'blog_posts';
const PUBLIC_BLOG_COLUMNS = `
  id,
  type,
  slug,
  title,
  description,
  body,
  category,
  audience,
  published_at,
  updated_at,
  status,
  cta_context,
  faqs,
  sources,
  created_at
`;

export function useBlogPosts() {
  return useQuery({
    queryKey: [TABLE, 'approved'],
    queryFn: async () => {
      // Tabela: blog_posts | Operacao: SELECT publico
      // Campos: conteudo, SEO, FAQ e fontes necessarios para renderizar a listagem.
      // Filtro: status = approved para impedir exposicao de rascunhos.
      // Riscos: conteudo publico; RLS deve permitir leitura anonima apenas de approved.
      const { data, error } = await supabase
        .from(TABLE)
        .select(PUBLIC_BLOG_COLUMNS)
        .eq('status', 'approved')
        .order('published_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as BlogPost[];
    },
  });
}

export function useBlogPost(slug: string) {
  return useQuery({
    queryKey: [TABLE, slug],
    queryFn: async () => {
      // Tabela: blog_posts | Operacao: SELECT publico por slug
      // Campos: conteudo completo do artigo, metadados SEO, FAQ e fontes.
      // Filtros: slug exato e status = approved para manter preview publico seguro.
      // Riscos: slug inexistente retorna null, nao erro tecnico para o usuario.
      const { data, error } = await supabase
        .from(TABLE)
        .select(PUBLIC_BLOG_COLUMNS)
        .eq('slug', slug)
        .eq('status', 'approved')
        .maybeSingle();
      if (error) throw error;
      return data as BlogPost | null;
    },
    enabled: !!slug,
  });
}

export function useAllBlogPostsAdmin() {
  return useQuery({
    queryKey: [TABLE, 'admin'],
    queryFn: async () => {
      // Tabela: blog_posts | Operacao: SELECT administrativo
      // Campos: todos os campos editoriais para criacao, revisao e publicacao.
      // Filtros: nenhum; a protecao vem de rota admin, Auth e politicas RLS.
      // Riscos: inclui rascunhos, portanto nao deve ser usado em pagina publica.
      const { data, error } = await supabase
        .from(TABLE)
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as BlogPost[];
    },
  });
}

export function useCreateBlogPost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (post: Omit<BlogPost, 'id' | 'created_at'>) => {
      // Tabela: blog_posts | Operacao: INSERT administrativo
      // Campos afetados: dados editoriais, SEO, FAQ, fontes e status de publicacao.
      // Riscos: publicar com status approved torna o artigo visivel no blog publico.
      const { data, error } = await supabase.from(TABLE).insert(post).select().single();
      if (error) throw error;
      return data as BlogPost;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [TABLE] }),
  });
}

export function useUpdateBlogPost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...patch }: Partial<BlogPost> & { id: string }) => {
      // Tabela: blog_posts | Operacao: UPDATE administrativo
      // Campos afetados: somente campos alterados e updated_at automatico.
      // Filtro: id exato do post para evitar alterar outros conteudos.
      // Riscos: mudar status para approved publica o artigo imediatamente.
      const { data, error } = await supabase
        .from(TABLE)
        .update({ ...patch, updated_at: new Date().toISOString().slice(0, 10) })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data as BlogPost;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [TABLE] }),
  });
}

export function useDeleteBlogPost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      // Tabela: blog_posts | Operacao: DELETE administrativo
      // Filtro: id exato do post; remocao e irreversivel pela interface.
      // Riscos: remove conteudo publico quando o post estiver approved.
      const { error } = await supabase.from(TABLE).delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [TABLE] }),
  });
}
